/**
 * Classic-scheme aim assist (08 §3). With 8-way movement and a 32-step aim the player cannot
 * point exactly at a man, so a carbine or pistol shot is pulled onto the nearest enemy he
 * could actually hit: alive, in range, in clear sight, and inside the assist cone around the
 * aim. Mouse and stick aiming send `aimAssist: 0`, and nothing here runs.
 */

import { type Ctx, isFighter } from '../ctx';
import { isAutomated } from '../enemies/specials/kinds';
import { angleDiff, atan2 } from '../math';
import { clearSight } from '../smoke';

/** Where the muzzle is, and the chest it is pointed at, for the sight test. */
const MUZZLE_HEIGHT = 1.4;
const CHEST_HEIGHT = 1.2;

/** The bearing the shot should take: onto the nearest eligible enemy, else the aim unchanged. */
export function assistedAim(ctx: Ctx, range: number): number {
  const { world: w, cmd } = ctx;
  const r = w.ranger;
  if (!(cmd.aimAssist > 0)) return r.aim;
  let best = r.aim;
  let bestD = range * range;
  const from = r.stance === 'crawl' ? 0.4 : MUZZLE_HEIGHT;
  for (const e of w.enemies) {
    // Armour is no target for a carbine, and a man already out of the fight is not worth the round.
    if (!isFighter(e) || isAutomated(e.kind)) continue;
    const dx = e.x - r.x;
    const dz = e.z - r.z;
    const d = dx * dx + dz * dz;
    if (d > bestD || d < 1e-6) continue;
    const bearing = atan2(dx, dz);
    if (Math.abs(angleDiff(cmd.aim, bearing)) > cmd.aimAssist) continue;
    if (!clearSight(ctx, r.x, r.z, from, e.x, e.z, e.state === 'down' ? 0.4 : CHEST_HEIGHT, e.mans ?? -1)) continue;
    bestD = d;
    best = bearing;
  }
  return best;
}
